/**
 * <community-videos> — the community-video grid (D-19.4).
 *
 * Mobile pagination: live ships this logic as a per-section inline IIFE; the
 * rebuild moves it into the house deferred-scoped-element shape. Behavior
 * identical: 8 tiles per page, pagination active below 750px only (desktop
 * shows all), prev/next disabled at the bounds, aria-current maintained, and
 * a debounced resize handler that un-hides everything at >=750px.
 *
 * Click-to-play (snippets/community-video.liquid): a Shopify-video tile ships
 * a poster <button> followed by an empty, hidden .video-container that carries
 * the video as data-video-* attributes. A click hides the poster, builds the
 * <video> into the container, reveals it and calls play() — synchronously,
 * inside the click, so it runs under the user gesture. This is live's inline
 * onclick flow; only the source of the element changed (built here instead of
 * cloned from a per-tile <template> of video_tag). A container that still
 * carries a <template> (the snippet's fallback) is cloned as before.
 */
if (!customElements.get('community-videos')) {
  // Mirrors video_tag's element: playsinline, the flags, preload="none", the
  // inline style, the poster and the <source> list in order.
  const buildVideo = (data) => {
    const video = document.createElement('video');
    video.setAttribute('playsinline', 'playsinline');
    if ('videoLoop' in data) video.setAttribute('loop', 'loop');
    if ('videoMuted' in data) {
      // The attribute alone only mutes a PARSED element; set the state too.
      video.setAttribute('muted', 'muted');
      video.muted = true;
    }
    if ('videoControls' in data) video.setAttribute('controls', 'controls');
    video.setAttribute('preload', 'none');
    video.setAttribute('style', 'width: 100%; height: 100%; background-color: #0a0a0a;');
    if (data.videoPoster) video.setAttribute('poster', data.videoPoster);
    data.videoSources.split(/,\s+/).forEach((entry) => {
      const [src, ...type] = entry.trim().split(/\s+/);
      if (!src) return;
      const source = document.createElement('source');
      source.setAttribute('src', src);
      if (type.length) source.setAttribute('type', type.join(' '));
      video.appendChild(source);
    });
    return video;
  };

  class CommunityVideos extends HTMLElement {
    constructor() {
      super();
      // Bound here, not in connectedCallback: it must work with or without
      // pagination (connectedCallback returns early at <=8 tiles), and bind
      // once even if the element is moved (theme editor reorder).
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button.video-section__poster');
        const container = button && button.nextElementSibling;
        if (container && container.classList.contains('video-container')) this.play(button, container);
      });
    }

    play(button, container) {
      button.style.display = 'none';
      if (!container.querySelector('video')) {
        const template = container.querySelector('template');
        if (template) {
          container.appendChild(template.content.cloneNode(true));
        } else if (container.dataset.videoSources) {
          container.appendChild(buildVideo(container.dataset));
        }
      }
      container.style.display = 'block';
      const video = container.querySelector('video');
      if (video) video.play();
    }

    connectedCallback() {
      this.itemsPerPage = 8;
      this.currentPage = 1;
      this.items = Array.from(this.querySelectorAll('.video-item'));
      this.pagination = this.querySelector('.video-pagination');
      if (!this.pagination || !this.items.length) return;
      this.totalPages = Math.ceil(this.items.length / this.itemsPerPage);

      this.prevButton = this.pagination.querySelector('.video-pagination__item--prev');
      this.nextButton = this.pagination.querySelector('.video-pagination__item--next');
      this.pageButtons = Array.from(this.pagination.querySelectorAll('[data-page]'));

      this.pageButtons.forEach((button) => {
        button.addEventListener('click', () => this.showPage(parseInt(button.dataset.page, 10)));
      });
      this.prevButton.addEventListener('click', () => {
        if (this.currentPage > 1) this.showPage(this.currentPage - 1);
      });
      this.nextButton.addEventListener('click', () => {
        if (this.currentPage < this.totalPages) this.showPage(this.currentPage + 1);
      });

      if (window.innerWidth < 750) this.showPage(1);

      this.onResize = () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
          if (window.innerWidth >= 750) {
            this.items.forEach((item) => item.classList.remove('video-item--hidden'));
          } else {
            this.showPage(this.currentPage);
          }
        }, 250);
      };
      window.addEventListener('resize', this.onResize);
    }

    disconnectedCallback() {
      if (this.onResize) window.removeEventListener('resize', this.onResize);
    }

    showPage(page) {
      this.currentPage = page;
      this.items.forEach((item) => item.classList.add('video-item--hidden'));
      const start = (page - 1) * this.itemsPerPage;
      const end = Math.min(start + this.itemsPerPage, this.items.length);
      for (let i = start; i < end; i += 1) {
        this.items[i].classList.remove('video-item--hidden');
      }
      this.pageButtons.forEach((button) => {
        const isCurrent = parseInt(button.dataset.page, 10) === page;
        button.classList.toggle('video-pagination__item--current', isCurrent);
        button.setAttribute('aria-current', isCurrent ? 'page' : 'false');
      });
      this.prevButton.disabled = page === 1;
      this.nextButton.disabled = page === this.totalPages;
    }
  }

  customElements.define('community-videos', CommunityVideos);
}
